import NotificationsService, {
    EXCLUDED_NOTIFICATION_TYPES,
} from "services/NotificationsService";

const { createAsyncThunk, createSlice } = require("@reduxjs/toolkit");

export const NOTIFICATIONS_PAGE_SIZE = 15;

export const getNotifications = createAsyncThunk(
    "notifications/getAll",
    async (page = 1, { rejectWithValue }) => {
        try {
            const response = await NotificationsService.getNotifications({
                page,
                limit: NOTIFICATIONS_PAGE_SIZE,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const getUnreadNotificationsCount = createAsyncThunk(
    "notifications/getUnreadCount",
    async (_, { rejectWithValue }) => {
        try {
            const response = await NotificationsService.getUnreadCount();
            return response.data.unreadCount;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const markNotificationAsRead = createAsyncThunk(
    "notifications/markAsRead",
    async (id, { rejectWithValue }) => {
        try {
            await NotificationsService.markAsRead(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const markAllNotificationsAsRead = createAsyncThunk(
    "notifications/markAllAsRead",
    async (_, { rejectWithValue }) => {
        try {
            await NotificationsService.markAllAsRead();
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

export const deleteNotification = createAsyncThunk(
    "notifications/delete",
    async (id, { rejectWithValue }) => {
        try {
            await NotificationsService.deleteNotification(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response || "Erreur");
        }
    }
);

const initialState = {
    items: [],
    unreadCount: 0,
    page: 1,
    total: 0,
    loading: false,
    error: null,
};

const notificationsSlice = createSlice({
    name: "notifications",
    initialState,
    reducers: {
        // Notification poussée en temps réel par le socket (`notification:new`)
        notificationReceived: (state, action) => {
            const notification = action.payload;
            if (
                !notification?._id ||
                EXCLUDED_NOTIFICATION_TYPES.includes(notification.meta?.type) ||
                state.items.some((item) => item._id === notification._id)
            ) {
                return;
            }
            state.items.unshift(notification);
            state.total += 1;
            if (!notification.isRead) state.unreadCount += 1;
        },
    },
    extraReducers: {
        [getNotifications.pending]: (state) => {
            state.loading = true;
            state.error = null;
        },
        [getNotifications.fulfilled]: (state, action) => {
            const { data = [], page = 1, total = 0 } = action.payload;
            state.loading = false;
            state.page = page;
            state.total = total;
            if (page === 1) {
                state.items = data;
            } else {
                const knownIds = new Set(state.items.map((item) => item._id));
                state.items.push(
                    ...data.filter((item) => !knownIds.has(item._id))
                );
            }
        },
        [getNotifications.rejected]: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },
        [getUnreadNotificationsCount.fulfilled]: (state, action) => {
            state.unreadCount = Number(action.payload) || 0;
        },
        [markNotificationAsRead.fulfilled]: (state, action) => {
            const item = state.items.find((n) => n._id === action.payload);
            if (item && !item.isRead) {
                item.isRead = true;
                state.unreadCount = Math.max(state.unreadCount - 1, 0);
            }
        },
        [markAllNotificationsAsRead.fulfilled]: (state) => {
            state.items.forEach((item) => {
                item.isRead = true;
            });
            state.unreadCount = 0;
        },
        [deleteNotification.fulfilled]: (state, action) => {
            const index = state.items.findIndex(
                (n) => n._id === action.payload
            );
            if (index === -1) return;
            if (!state.items[index].isRead) {
                state.unreadCount = Math.max(state.unreadCount - 1, 0);
            }
            state.items.splice(index, 1);
            state.total = Math.max(state.total - 1, 0);
        },
    },
});

export const { notificationReceived } = notificationsSlice.actions;

export default notificationsSlice.reducer;
